/**
 * Lenders admin page.
 *
 * Full CRUD:
 *   - List all lenders with logo previews
 *   - Search + filter (active/featured/finance type)
 *   - Add new lender
 *   - Edit lender fields inline (dialog)
 *   - Upload / replace / remove logo per lender
 *   - Toggle active/featured
 *   - Delete or deactivate
 *   - Bulk ZIP import for logos
 *
 * Admin/manager only. Regular brokers see a read-only directory.
 */
import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Filter,
  Loader2,
  Plus,
  Search,
  Star,
  StarOff,
  Trash2,
  Upload,
  UploadCloud,
  X,
  Pencil,
  Image as ImageIcon,
  FileArchive,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { LenderLogo } from "@/components/LenderLogo"
import {
  lendersApi,
  ApiError,
  type Lender,
  type LenderInput,
} from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { cn } from "@/lib/utils"

export default function LendersPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === "admin" || user?.role === "manager"

  const qc = useQueryClient()
  const [query, setQuery] = useState("")
  const [activeFilter, setActiveFilter] = useState<"true" | "false" | "all">("true")
  const [financeFilter, setFinanceFilter] = useState<"consumer" | "commercial" | "all">("all")
  const [featuredOnly, setFeaturedOnly] = useState(false)
  const [editing, setEditing] = useState<Lender | null>(null)
  const [creating, setCreating] = useState(false)
  const [zipOpen, setZipOpen] = useState(false)

  const list = useQuery({
    queryKey: [
      "lenders",
      { active: activeFilter, finance: financeFilter, q: query, featured: featuredOnly },
    ],
    queryFn: () =>
      lendersApi.list({
        q: query || undefined,
        active: activeFilter,
        finance: financeFilter,
        featured: featuredOnly ? "true" : "all",
        sort: "sort_order",
      }),
  })

  const seed = useMutation({
    mutationFn: () => lendersApi.seed(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lenders"] }),
  })

  const items = list.data?.items ?? []

  return (
    <AppShell
      title="Lenders"
      subtitle="Lender directory + logos"
      actions={
        isAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setZipOpen(true)}>
              <FileArchive className="h-4 w-4" />
              Bulk logos
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add lender
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search lenders…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-muted-foreground h-4 w-4" />
              <select
                className="border-input bg-background h-10 rounded-md border px-3 text-sm"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as "true" | "false" | "all")}
              >
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
                <option value="all">All</option>
              </select>
              <select
                className="border-input bg-background h-10 rounded-md border px-3 text-sm"
                value={financeFilter}
                onChange={(e) =>
                  setFinanceFilter(e.target.value as "consumer" | "commercial" | "all")
                }
              >
                <option value="all">All products</option>
                <option value="consumer">Consumer</option>
                <option value="commercial">Commercial</option>
              </select>
              <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={featuredOnly}
                  onChange={(e) => setFeaturedOnly(e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                Featured
              </label>
            </div>
          </div>
        </Card>

        {/* Empty state / list */}
        {list.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-16 text-center">
            <ImageIcon className="text-muted-foreground mx-auto mb-4 h-10 w-10 opacity-50" />
            <h3 className="text-lg font-semibold text-foreground">No lenders yet</h3>
            <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
              {query || activeFilter !== "true" || financeFilter !== "all" || featuredOnly
                ? "Try adjusting the filters."
                : "Seed the default list of 47 Australian lenders or add one manually."}
            </p>
            {isAdmin &&
              !query &&
              activeFilter === "true" &&
              financeFilter === "all" &&
              !featuredOnly && (
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <Button
                    onClick={() => seed.mutate()}
                    disabled={seed.isPending}
                  >
                    {seed.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Seed default lenders
                  </Button>
                </div>
              )}
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-border bg-rc-grey-50 text-muted-foreground border-b text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Lender</th>
                  <th className="px-4 py-3 text-left font-medium">Products</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Website</th>
                  {isAdmin && <th className="px-4 py-3 text-right"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <LenderRow
                    key={l.id}
                    lender={l}
                    canEdit={isAdmin}
                    onEdit={() => setEditing(l)}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      {editing && (
        <LenderEditDialog
          lender={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {creating && (
        <LenderEditDialog
          lender={null}
          onClose={() => setCreating(false)}
        />
      )}
      {zipOpen && <BulkZipDialog onClose={() => setZipOpen(false)} />}
    </AppShell>
  )
}

// ============================================================
// Table row
// ============================================================

function LenderRow({
  lender,
  canEdit,
  onEdit,
}: {
  lender: Lender
  canEdit: boolean
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const toggleFeatured = useMutation({
    mutationFn: () =>
      lendersApi.update(lender.id, { isFeatured: !lender.isFeatured }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lenders"] }),
  })
  const toggleActive = useMutation({
    mutationFn: () => lendersApi.update(lender.id, { isActive: !lender.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lenders"] }),
  })

  const products = [
    lender.consumerEnabled && "Consumer",
    lender.commercialEnabled && "Commercial",
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <tr
      className={cn(
        "border-border border-b hover:bg-rc-grey-50",
        !lender.isActive && "opacity-60"
      )}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          <LenderLogo name={lender.name} logoUrl={lender.logoUrl} size="md" />
          <div className="min-w-0">
            <div className="text-foreground truncate text-sm font-medium">
              {lender.displayName ?? lender.name}
              {lender.isFeatured && (
                <Star className="ml-1 inline h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              )}
            </div>
            <div className="text-muted-foreground truncate text-xs">
              {lender.slug}
              {lender.legalName && lender.legalName !== lender.name && (
                <> · {lender.legalName}</>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="text-muted-foreground px-4 py-2.5 text-xs">
        {products || "—"}
        {lender.financeProducts.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {lender.financeProducts.slice(0, 4).map((p) => (
              <span
                key={p}
                className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 text-[10px]"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span
          className={cn(
            "inline-block rounded px-2 py-0.5 text-[10px] font-medium",
            lender.isActive
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-700"
          )}
        >
          {lender.isActive ? "Active" : "Inactive"}
        </span>
        {lender.hasLogo ? (
          <span className="ml-1.5 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
            Logo
          </span>
        ) : (
          <span className="ml-1.5 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            No logo
          </span>
        )}
      </td>
      <td className="px-4 py-2.5">
        {lender.websiteUrl ? (
          <a
            href={lender.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs"
          >
            Website
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
      {canEdit && (
        <td className="px-4 py-2.5 text-right">
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => toggleFeatured.mutate()}
              disabled={toggleFeatured.isPending}
              className="text-muted-foreground hover:text-amber-500 p-1.5"
              title={lender.isFeatured ? "Remove from featured" : "Mark as featured"}
            >
              {lender.isFeatured ? (
                <StarOff className="h-3.5 w-3.5" />
              ) : (
                <Star className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => toggleActive.mutate()}
              disabled={toggleActive.isPending}
              className={cn(
                "text-muted-foreground p-1.5",
                lender.isActive ? "hover:text-red-600" : "hover:text-emerald-600"
              )}
              title={lender.isActive ? "Deactivate" : "Reactivate"}
            >
              {lender.isActive ? (
                <X className="h-3.5 w-3.5" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="text-muted-foreground hover:text-rc-blue-600 p-1.5"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  )
}

// ============================================================
// Edit dialog (also handles Create when lender=null)
// ============================================================

function LenderEditDialog({
  lender,
  onClose,
}: {
  lender: Lender | null
  onClose: () => void
}) {
  const isCreate = lender === null
  const qc = useQueryClient()
  const [form, setForm] = useState<LenderInput>({
    slug: lender?.slug ?? "",
    name: lender?.name ?? "",
    displayName: lender?.displayName ?? null,
    legalName: lender?.legalName ?? null,
    websiteUrl: lender?.websiteUrl ?? null,
    consumerEnabled: lender?.consumerEnabled ?? false,
    commercialEnabled: lender?.commercialEnabled ?? false,
    financeProducts: lender?.financeProducts ?? [],
    isFeatured: lender?.isFeatured ?? false,
    sortOrder: lender?.sortOrder ?? 100,
  })
  const [error, setError] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      if (isCreate) {
        const created = await lendersApi.create(form)
        if (logoFile) await lendersApi.uploadLogo(created.id, logoFile)
        return created
      }
      await lendersApi.update(lender!.id, form)
      if (logoFile) await lendersApi.uploadLogo(lender!.id, logoFile)
      return { ok: true }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lenders"] })
      onClose()
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Failed")
    },
  })

  const removeLogo = useMutation({
    mutationFn: () => lendersApi.removeLogo(lender!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lenders"] }),
  })

  const del = useMutation({
    mutationFn: () => lendersApi.remove(lender!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lenders"] })
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Delete failed"),
  })

  const onFilePicked = (f: File | null) => {
    setLogoFile(f)
    if (f) {
      const url = URL.createObjectURL(f)
      setLogoPreview(url)
    } else {
      setLogoPreview(null)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add lender" : `Edit ${lender!.name}`}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 pt-2 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Pepper Money"
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="pepper-money"
                disabled={!isCreate}
              />
              <p className="text-muted-foreground mt-1 text-[11px]">
                URL-safe identifier. Cannot change after creation.
              </p>
            </div>
            <div>
              <Label htmlFor="displayName">Display name</Label>
              <Input
                id="displayName"
                value={form.displayName ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, displayName: e.target.value || null }))
                }
                placeholder="Optional short label"
              />
            </div>
            <div>
              <Label htmlFor="legalName">Legal name</Label>
              <Input
                id="legalName"
                value={form.legalName ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, legalName: e.target.value || null }))
                }
                placeholder="Pepper Money Limited"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={form.websiteUrl ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, websiteUrl: e.target.value || null }))
                }
                placeholder="https://www.pepper.com.au"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label>Logo</Label>
              <div className="mt-1 flex items-start gap-3">
                <LenderLogo
                  name={form.name || "?"}
                  logoUrl={logoPreview ?? lender?.logoUrl ?? null}
                  size="lg"
                />
                <div className="flex-1 space-y-2">
                  <label className="hover:border-rc-blue-300 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 p-2 text-sm">
                    <Upload className="h-4 w-4" />
                    <span>{logoFile ? logoFile.name : "Upload SVG, PNG, WebP, or JPEG"}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".svg,.png,.webp,.jpg,.jpeg,image/svg+xml,image/png,image/webp,image/jpeg"
                      onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  <p className="text-muted-foreground text-[11px]">
                    Max 2 MB. Official transparent SVG preferred.
                  </p>
                  {lender?.logoUrl && !logoFile && (
                    <button
                      type="button"
                      onClick={() => removeLogo.mutate()}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove existing logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label>Finance categories</Label>
              <div className="mt-1 space-y-1.5">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consumerEnabled}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, consumerEnabled: e.target.checked }))
                    }
                    className="h-4 w-4 rounded"
                  />
                  Consumer finance
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.commercialEnabled}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, commercialEnabled: e.target.checked }))
                    }
                    className="h-4 w-4 rounded"
                  />
                  Commercial finance
                </label>
              </div>
            </div>

            <div>
              <Label htmlFor="products">
                Finance products (comma-separated)
              </Label>
              <Input
                id="products"
                value={form.financeProducts?.join(", ") ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    financeProducts: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  }))
                }
                placeholder="vehicle, equipment, personal"
              />
            </div>

            <div>
              <Label htmlFor="sortOrder">Sort order</Label>
              <Input
                id="sortOrder"
                type="number"
                value={form.sortOrder ?? 100}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
                }
              />
              <p className="text-muted-foreground mt-1 text-[11px]">
                Lower = appears first. Big 4 = 10-13, featured 15-30, rest 100.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.isFeatured ?? false}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isFeatured: e.target.checked }))
                }
                className="h-4 w-4 rounded"
              />
              <Star className="h-4 w-4 text-amber-500" />
              Featured lender (shows at top of selector)
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div>
            {!isCreate && (
              <Button
                variant="outline"
                onClick={() => {
                  if (
                    confirm(
                      `Delete ${lender!.name}? If any deals reference this lender it will be deactivated instead.`
                    )
                  ) {
                    del.mutate()
                  }
                }}
                disabled={del.isPending}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                {del.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !form.name || !form.slug}
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isCreate ? "Create lender" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Bulk ZIP import dialog
// ============================================================

type ZipResult = {
  filename: string
  slug: string
  matched: boolean
  lenderId?: string
  contentType?: string
  error?: string
}

function BulkZipDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<
    | { processed: number; results: ZipResult[]; stillMissing: string[] }
    | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (f: File) => {
      const res = await lendersApi.importZip(f)
      return res as {
        ok: true
        processed: number
        results: ZipResult[]
        stillMissing: string[]
      }
    },
    onSuccess: (data) => {
      setResult({
        processed: data.processed,
        results: data.results,
        stillMissing: data.stillMissing,
      })
      qc.invalidateQueries({ queryKey: ["lenders"] })
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : "Upload failed"),
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk logo import</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 pt-2">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              Processed {result.processed} files ·{" "}
              {result.results.filter((r) => r.matched && !r.error).length} uploaded ·{" "}
              {result.results.filter((r) => r.error).length} errors
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="p-2 text-left">File</th>
                    <th className="p-2 text-left">Slug</th>
                    <th className="p-2 text-left">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={i} className="border-border border-t">
                      <td className="p-2 font-mono">{r.filename}</td>
                      <td className="p-2">{r.slug || "—"}</td>
                      <td className="p-2">
                        {r.error ? (
                          <span className="text-red-700">✕ {r.error}</span>
                        ) : (
                          <span className="text-emerald-700">
                            ✓ {r.contentType?.split("/")[1] ?? "uploaded"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.stillMissing.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="font-medium mb-1">
                  {result.stillMissing.length} lenders still without a logo:
                </div>
                <div className="max-h-24 overflow-y-auto">
                  {result.stillMissing.join(", ")}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              Upload a ZIP containing logo files named after each lender's slug. Files
              matching a lender slug (e.g. <code className="bg-slate-100 px-1">pepper-money.svg</code>) will be uploaded automatically.
              Unmatched files are reported for manual review.
            </p>
            <label className="hover:border-rc-blue-300 flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-8 text-sm">
              <UploadCloud className="h-8 w-8 text-slate-400" />
              <span>{file ? file.name : "Click to select ZIP file"}</span>
              <span className="text-muted-foreground text-[11px]">
                Max 20 MB. SVG/PNG/WebP/JPEG allowed inside.
              </span>
              <input
                type="file"
                accept=".zip"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <AlertCircle className="mr-1 inline h-4 w-4" />
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => file && upload.mutate(file)}
                disabled={!file || upload.isPending}
              >
                {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Upload & import
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
